import { useEffect, useMemo } from 'react';
// Import Types
import type { ParsedWhatsAppTemplate } from 'components/Template/WhatsApp/WhatsAppTemplatePreview/types';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import {
    clearWhatsAppTemplateByLanguage,
    selectIsWhatsAppTemplatesByLanguageLoading,
    selectWhatsAppTemplatesByLanguage,
    setWhatsAppTemplateByLanguage,
    setWhatsAppTemplateByLanguageLoading,
} from 'store/slices/whatsAppTemplate';
// Import Hooks
import { useRequest } from 'hooks';
// Import Services
import { TemplateHttpService } from 'services/http';
// Import Constants
import { WhatsAppTemplateStatusLabels } from 'constants/common';

interface UseWhatsAppPreviewProps {
    body: string;
    enabled?: boolean;
}

const useWhatsAppPreview = ({ body, enabled = true }: UseWhatsAppPreviewProps) => {
    const { doGetRequest } = useRequest();
    const dispatch = useAppDispatch();

    const templatesByLanguage = useAppSelector(selectWhatsAppTemplatesByLanguage);
    const isLoadingTemplatesByLanguage = useAppSelector(selectIsWhatsAppTemplatesByLanguageLoading);

    const parsedWhatsAppTemplate: ParsedWhatsAppTemplate | null = useMemo(() => {
        if (!enabled) return null;

        try {
            return JSON.parse(body) as ParsedWhatsAppTemplate;
        } catch {
            return null;
        }
    }, [body, enabled]);

    const { getWhatsAppTemplates } = useMemo(
        () => ({
            // eslint-disable-next-line no-restricted-syntax -- an abort would leave the isLoadingTemplatesByLanguage store flag stuck
            getWhatsAppTemplates: TemplateHttpService.getWhatsAppTemplates(),
        }),
        [],
    );

    const templateInfo = useMemo(() => {
        if (!enabled || !parsedWhatsAppTemplate) {
            return { components: null, status: null, isLoading: false };
        }

        const selectedTemplate = templatesByLanguage?.find(
            (template) =>
                template.Name === parsedWhatsAppTemplate.TemplateName &&
                template.Language === parsedWhatsAppTemplate.TemplateLanguage,
        );

        return {
            components: selectedTemplate?.Components,
            status: WhatsAppTemplateStatusLabels[selectedTemplate?.Status ?? ''],
            isLoading: isLoadingTemplatesByLanguage,
        };
    }, [enabled, templatesByLanguage, parsedWhatsAppTemplate, isLoadingTemplatesByLanguage]);

    const templateName = parsedWhatsAppTemplate?.TemplateName;
    const hasMatchingData = templatesByLanguage?.length && templatesByLanguage.some((t) => t?.Name === templateName);
    const needsFetch = enabled && templateName && !isLoadingTemplatesByLanguage && !hasMatchingData;

    useEffect(
        () => {
            if (!needsFetch) return;

            if (templatesByLanguage?.length && !hasMatchingData) {
                dispatch(clearWhatsAppTemplateByLanguage());
            }

            dispatch(setWhatsAppTemplateByLanguageLoading(true));
            doGetRequest(getWhatsAppTemplates.request, {
                queryString: { templateName },
                successCallback: (data) => {
                    dispatch(setWhatsAppTemplateByLanguage(data));
                    dispatch(setWhatsAppTemplateByLanguageLoading(false));
                },
                errorCallback: () => {
                    dispatch(setWhatsAppTemplateByLanguageLoading(false));
                },
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [enabled, templateName],
    );
    return templateInfo;
};

export { useWhatsAppPreview };
